import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    Document,
    FalconNavigatorHeader,
    ICustomerPortalService,
    PartyProperty,
    Policy,
    productHelper,
    ProductsEnum,
    resourcesHelper,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {map} from "rxjs/operators";
import {RouteComponentProps} from "react-router-dom";
import {ActiveDocumentsPanel} from "./ActiveDocumentsPanel";
import FalconHeader from "../../../components/navigation/FalconHeader";
import FalconFooter from "../../../components/navigation/FalconFooter";
import {mapDocumentTitles} from "../../../utils/DataMappers";
import {downloadDocument, trimFileExtension} from "../../../CommonFunctions";

interface IProps extends RouteComponentProps<{}> {}

interface IState {
    policies?: Policy[];
    archivePolicies?: Policy[];
    carsDocuments: Document[];
    rentersDocuments: Document[];
    archiveDocuments: Document[];
    documentsLoaded: boolean;
    archiveDocumentsLoaded: boolean;
    direction: string;
    active: boolean;
    isWhite: boolean;
    day: number;
    showHistory: boolean;
    isDocumentDownloaded: boolean;
    oldMissedPaymentProcessProperty: PartyProperty | null;
    firstRenewalExecutedProperty: PartyProperty | null;
    showBothDocuments: boolean | null;
}

@authorize
export class DocumentsPage extends React.Component<IProps, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly OLD_MISSED_PAYMENT_PROCESS: string = "oldMissedPaymentProcess";
    private readonly FIRST_RENEWAL_EXECUTED: string = "firstRenewalExecuted";
    private originalLatestQuoteDate: moment.Moment;

    constructor(props: Readonly<IProps>) {
        super(props);
        this.state = {
            documentsLoaded: false,
            archiveDocumentsLoaded: false,
            direction: 'asc',
            carsDocuments: [],
            rentersDocuments: [],
            archiveDocuments: [],
            active: false,
            isWhite: true,
            day: 0,
            showHistory: false,
            isDocumentDownloaded: true,
            oldMissedPaymentProcessProperty: null,
            firstRenewalExecutedProperty: null,
            showBothDocuments: null
        };

        this.sortByDate = this.sortByDate.bind(this);
        this.renderLoadMoreButton = this.renderLoadMoreButton.bind(this);
        this.showMore = this.showMore.bind(this);
        this.prepareStaticDocuments = this.prepareStaticDocuments.bind(this);
        this.setDocuments = this.setDocuments.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy documents");

        this.getDocuments();
    }

    public render() {
        const loading = !(this.state.archiveDocumentsLoaded && this.state.documentsLoaded && this.state.isDocumentDownloaded);
        const coverTier = this.state.policies ? this.state.policies[0].interaction.coverTier : "ALLIANZ";

        if(this.state.policies){
            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="plugin">
                        <FalconNavigatorHeader
                            back="/dashboard/monthlySubscription"
                            title="Your documents"
                        />

                        {this.renderDocumentsBanner()}

                        {this.state.carsDocuments && <ActiveDocumentsPanel
                            description="Car documents"
                            documents={this.state.carsDocuments}
                            imageLink={`/${resourcesHelper.THEME}/img/wolf-car-icon.png`}
                            showBothDocuments={this.state.showBothDocuments!}
                            originalStartDate={this.originalLatestQuoteDate.toDate()}
                            product={ProductsEnum.SUBSCRIPTION}
                            coverTier={coverTier}
                        />}

                        {this.state.rentersDocuments && <ActiveDocumentsPanel
                            description="Renters documents"
                            documents={this.state.rentersDocuments}
                            imageLink={"/img/Flow-renters.svg"}
                            showBothDocuments={this.state.showBothDocuments!}
                            originalStartDate={this.originalLatestQuoteDate.toDate()}
                            product={ProductsEnum.RENTERS}
                            coverTier={coverTier}
                        />}
                        {this.renderDocumentsArchivePanel()}
                        {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/monthlySubscription")}
                    </div>

                    <Overlay show={loading} />
                    <FalconFooter/>
                </React.Fragment>
            );
        } else {
            return <></>;
        }
    }

    public renderDocumentsArchivePanel(): React.JSX.Element {
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            this.state.archiveDocuments.sort((a, b) => moment(b.date).diff(a.date));
            return (
                <div className="widget documents-widget panel mx-3 documents-archive px-md-0 px-3">
                    <p className={"font-weight-bold text-center"}>Documents archive</p>
                    <table>
                        <tbody>
                            {this.renderTableHeader()}
                            {this.renderArchivedDocumentsByDate()}
                        </tbody>
                    </table>
                    {this.renderLoadMoreButton()}
                </div>
            );
        } else {
            return <></>;
        }
    }

    public renderDocumentsBanner(): React.JSX.Element {
        return (
            <div id="documentsBanner" className="banner wide-gutters panel-width banner-info">
                <div className="banner-text">
                    <p>
                    Heads up - we'll send your documents by post for the 1st month, if you asked us to do so.
                    After that your documents will be available to view and download at any time here.
                    If you would like to receive your documents by post, then <a className="btn-text p-0" href={resourcesHelper.COMPLAINT_EMAIL} target="_blank">email our customer service team</a> to request them.
                    </p>
                </div>
            </div>
        )
    }

    public downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContent: ArrayBuffer) => {
                if (documentContent) {
                    downloadDocument(documentContent, doc.name);
                }
                this.setState({isDocumentDownloaded: true});
            }
        );
    };

    private getDocuments(): void {
        this.customerPortalService
            .getPolicies()
            .pipe(map((policies: Policy[]) => policies.filter((policy: Policy) =>
                !policy.endDate || moment(policy.endDate).isSameOrAfter(moment().startOf("day"))
                && !productHelper.includesWolfMotorProduct([policy])
            )))
            .subscribe((policies: Policy[]) => {
                this.setState({policies}, this.prepareStaticDocuments);

                if (policies && policies.length > 0) {
                    this.customerPortalService
                        .getAllDocuments()
                        .subscribe((documents: Document[]) => {
                            const carsDocuments: Document[] = [];
                            const rentersDocuments: Document[] = [];
                            const archiveDocuments: Document[] = [];

                            policies.forEach((policy: Policy) => {
                                const allPolicyDocs = documents.filter(doc => {
                                    return doc.tags.filter(d => d === ("agreement_" + policy.policyNumber)).length > 0
                                });

                                const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                                    return doc.tags.filter(d => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
                                });

                                const mappedCurrentDocuments = mapDocumentTitles(currentPolicyVersionDocs, policy);
                                if (productHelper.includesMotorProduct([policy])) {
                                    carsDocuments.push(...mappedCurrentDocuments);
                                }
                                if (productHelper.includesRentersProduct([policy])) {
                                    rentersDocuments.push(...mappedCurrentDocuments);
                                }

                                archiveDocuments.push(...mapDocumentTitles(allPolicyDocs, policy));
                            });

                            this.setState({
                                carsDocuments,
                                rentersDocuments,
                                archiveDocuments,
                                documentsLoaded: true,
                                archiveDocumentsLoaded: true
                            })
                        });
                } else {
                    this.setState({documentsLoaded: true, archiveDocumentsLoaded: true})
                }
            });
    }

    private renderTableHeader(): React.JSX.Element {
        return (
            <React.Fragment>
                <tr>
                    <th className={"pb-3 pl-3"}>
                        <span className="muli-semi-bold-16">Date</span>
                        <span onClick={this.sortByDate} className={`${this.state.active &&
                        (this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down")}
                            ${!this.state.active && "fas fa-sort"} table-sort-arrow`}/>
                    </th>
                </tr>
            </React.Fragment>
        )
    }

    private renderArchivedDocumentsByDate(): React.JSX.Element[] | string {
        let documents: React.JSX.Element[];
        let day: number = 0;
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            let previousDate: string | null = "";
            let isWhite: boolean = true;
            documents = this.state.archiveDocuments.map((document, index) => {
                const {date, title, name} = document;
                if (PluginTemplates.convertDate(date, "DD MMMM YYYY") === previousDate) {
                    const backGround: string = isWhite? "" : "light-blue-background";
                    const history: string = day >= 4? "display-none": "";
                    return (
                        <tr key={index} className={`${backGround} ${history} ${this.state.showHistory? "display-block": ""}`}>
                            <td id="document-date" className="muli-semi-bold-16">
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"} onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page" onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon" src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                } else {
                    previousDate = PluginTemplates.convertDate(date, "DD MMMM YYYY");
                    isWhite = !isWhite;
                    day += 1;
                    const backGround: string = isWhite? "" : "light-blue-background";
                    const history: string = day >= 4? "display-none": "";
                    return (
                        <tr key={index} className={`${backGround} ${history} ${this.state.showHistory? "display-block": ""}`}>
                            <td id="document-date" className="muli-semi-bold-16 pt-3">
                                <span className="font-weight-extra-light fs-14 ml-2">{PluginTemplates.convertDate(date, "DD MMMM YYYY")}</span>
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"} onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page" onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon" src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                }

            });
        } else {
            return "";
        }
        if (day !== this.state.day) {
            this.setState({day});
        }

        return documents;
    }

    private renderLoadMoreButton(): React.JSX.Element {
        if (!this.state.day) {
            return <></>;
        }
        if (this.state.day >= 4) {
            return (
                <button type="button" className="btn btn-primary btn-next plugin"
                        onMouseDown={this.showMore}
                        onTouchStart={this.showMore}
                        id={"show-more"}>
                    Load more
                </button>
            )
        }

        return <></>;
    }

    private showMore(): void {
        this.setState({
            showHistory: true
        })
    }

    private sortByDate(): void {
        const sortedDates = this.state.direction === 'asc' ?
            this.state.archiveDocuments!.sort((a, b) => moment(a.date).diff(b.date))
            :
            this.state.archiveDocuments!.sort((a, b) => moment(b.date).diff(a.date));
        this.setState({
            archiveDocuments: sortedDates,
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    }

    private prepareStaticDocuments(): void {
        this.customerPortalService.getPartyPropertyByPropertyKey(this.OLD_MISSED_PAYMENT_PROCESS).subscribe(
            (oldMissedPaymentProcessProperty: PartyProperty) => {
                this.setState({oldMissedPaymentProcessProperty});

                this.customerPortalService.getPartyPropertyByPropertyKey(this.FIRST_RENEWAL_EXECUTED).subscribe(
                    (firstRenewalExecutedProperty: PartyProperty) => {
                        this.setState({firstRenewalExecutedProperty}, this.setDocuments);
                    });
            });
    }

    private setDocuments(): void {
        if (this.state.policies && productHelper.includesMotorProduct(this.state.policies!)) {
            if (this.state.policies![0].interaction && this.state.policies![0].interaction.factors && this.state.policies![0].interaction.factors.originalQuoteDate) {
                const originalQuoteDateRaw = this.state.policies![0].interaction.renewalDetails?.periodStartDate ?? this.state.policies![0].interaction.factors.originalQuoteDate;
                this.originalLatestQuoteDate = moment.utc(originalQuoteDateRaw, moment.ISO_8601);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/subscription/DocumentsPage.tsx